/**
 * apply(state, event) folds one event into the state.
 *
 * It trusts the event: every check already happened in decide() before the event was
 * stored. That is why apply has no error cases; it only copies facts into the state.
 *
 * For speed, apply updates `state` in place and returns it. The state object belongs to
 * whoever is folding (see fold()), so nothing else ever sees a half-updated state.
 */
import type { EventBody } from "./events.ts";
import { emptyState, receiptKey, type GroupState } from "./types.ts";

export function apply(state: GroupState, event: EventBody): GroupState {
  switch (event.type) {
    case "group.created":
      state.name = event.data.name;
      return state;

    case "settings.updated":
      state.settings = { ...state.settings, ...event.data.after };
      return state;

    case "store.set":
      if (event.data.after) state.stores[event.data.name] = { ...event.data.after };
      else delete state.stores[event.data.name];
      return state;

    case "person.added":
      state.people[event.data.person.id] = { ...event.data.person };
      state.personOrder.push(event.data.person.id);
      return state;

    case "person.updated": {
      const p = state.people[event.data.personId];
      if (p) state.people[p.id] = { ...p, ...event.data.after };
      return state;
    }

    case "person.removed":
      delete state.people[event.data.person.id];
      state.personOrder = state.personOrder.filter((id) => id !== event.data.person.id);
      return state;

    case "item.added": {
      const item = event.data.item;
      state.items[item.id] = { ...item, ticked: [...item.ticked] };
      const pos = event.data.position;
      if (pos === null || pos >= state.itemOrder.length) state.itemOrder.push(item.id);
      else state.itemOrder.splice(Math.max(0, pos), 0, item.id);
      return state;
    }

    case "item.updated": {
      const it = state.items[event.data.itemId];
      if (it) state.items[it.id] = { ...it, ...event.data.after };
      return state;
    }

    case "item.removed":
      delete state.items[event.data.item.id];
      state.itemOrder = state.itemOrder.filter((id) => id !== event.data.item.id);
      return state;

    case "share.set": {
      const it = state.items[event.data.itemId];
      if (!it) return state;
      const without = it.ticked.filter((p) => p !== event.data.personId);
      state.items[it.id] = {
        ...it,
        ticked: event.data.ticked ? [...without, event.data.personId] : without,
      };
      return state;
    }

    case "payment.added":
      state.payments[event.data.payment.id] = { ...event.data.payment };
      state.paymentOrder.push(event.data.payment.id);
      return state;

    case "payment.updated": {
      const p = state.payments[event.data.paymentId];
      if (p) state.payments[p.id] = { ...p, ...event.data.after };
      return state;
    }

    case "payment.removed":
      delete state.payments[event.data.payment.id];
      state.paymentOrder = state.paymentOrder.filter((id) => id !== event.data.payment.id);
      return state;

    case "receipt.set": {
      const key = receiptKey(event.data);
      if (event.data.after === null) delete state.receipts[key];
      else state.receipts[key] = event.data.after;
      return state;
    }

    case "catalog.noted": {
      state.catalogNotes ??= {};
      if (event.data.after) state.catalogNotes[event.data.key] = { ...event.data.after };
      else delete state.catalogNotes[event.data.key];
      return state;
    }

    case "import.marked":
      return state;

    default: {
      // If a new event type is added and not handled above, this line stops compiling.
      const unhandled: never = event;
      return unhandled;
    }
  }
}

/** Rebuilds a group's state from its events, oldest first. */
export function fold(groupId: string, events: Iterable<EventBody>): GroupState {
  let state = emptyState(groupId);
  for (const e of events) state = apply(state, e);
  return state;
}

/** Deep copy, for callers that need to keep an old state while folding further. */
export function cloneState(state: GroupState): GroupState {
  return structuredClone(state);
}
